import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth.jsx';
import {
  collection, getDocs, getDoc, doc, updateDoc,
  query, orderBy, where, Timestamp
} from 'firebase/firestore';

const GRID_COLS = '2.4fr 1fr 80px 130px 32px';

function relativeTime(date) {
  if (!date) return 'Never';
  const days = Math.floor((Date.now() - date) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return date.toLocaleDateString();
}

function fmtDuration(secs) {
  if (!secs) return '—';
  const m = Math.round(secs / 60);
  return m < 60 ? `${m} min` : `${(m / 60).toFixed(1)}h`;
}

function initials(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

function toDate(val) {
  if (!val) return null;
  if (val instanceof Timestamp) return val.toDate();
  if (val?.toDate) return val.toDate();
  return new Date(val);
}

const GRADIENTS = [
  'linear-gradient(135deg,#60a5fa,#2563eb)',
  'linear-gradient(135deg,#a78bfa,#7c3aed)',
  'linear-gradient(135deg,#2dd4bf,#0d9488)',
  'linear-gradient(135deg,#fb7185,#e11d48)',
  'linear-gradient(135deg,#fbbf24,#d97706)',
  'linear-gradient(135deg,#4ade80,#16a34a)',
];

function avatarGrad(uid) {
  let h = 0;
  for (const c of uid) h = c.charCodeAt(0) + ((h << 5) - h);
  return GRADIENTS[Math.abs(h) % GRADIENTS.length];
}

function actStatus(lastActivity) {
  if (!lastActivity) return 'idle';
  const d = (Date.now() - lastActivity) / 86400000;
  return d <= 7 ? 'active' : d <= 30 ? 'recent' : 'idle';
}

function StatusDot({ status }) {
  const cls =
    status === 'active' ? 'bg-emerald-500 shadow-[0_0_0_3px_#d1fae5]' :
    status === 'recent' ? 'bg-amber-400 shadow-[0_0_0_3px_#fef3c7]' :
    'bg-gray-300';
  return <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cls}`} />;
}

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [drills, setDrills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState(null);
  const [pendingBelt, setPendingBelt] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingDrill, setEditingDrill] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [userRole, setUserRole] = useState(null);
  const [currentSchoolId, setCurrentSchoolId] = useState(null);
  const [schoolBelts, setSchoolBelts] = useState([]);
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser) return;
    const checkAccess = async () => {
      try {
        const tokenResult = await currentUser.getIdTokenResult();
        if (tokenResult.claims.role === 'super-admin') {
          setUserRole('super-admin');
          await loadData('super-admin', null);
          return;
        }
        const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
        const schoolId = userSnap.data()?.profile?.schoolId;
        if (!schoolId) { navigate('/home'); return; }
        const schoolSnap = await getDoc(doc(db, 'schools', schoolId));
        const schoolData = schoolSnap.data() || {};
        const isSchoolAdmin = (schoolData.adminIds || []).includes(currentUser.uid);
        const isInstructor = (schoolData.instructorIds || []).includes(currentUser.uid);
        if (!isSchoolAdmin && !isInstructor) { navigate('/home'); return; }
        const role = isSchoolAdmin ? 'school-admin' : 'instructor';
        setUserRole(role);
        setCurrentSchoolId(schoolId);
        await loadData(role, schoolId);
      } catch (err) {
        console.error('Error checking access:', err);
        navigate('/home');
      }
    };
    checkAccess();
  }, [currentUser, navigate]);

  const loadData = async (role, schoolId) => {
    try {
      setLoading(true);
      const usersQ = schoolId
        ? query(collection(db, 'users'), where('profile.schoolId', '==', schoolId))
        : collection(db, 'users');
      const usersSnapshot = await getDocs(usersQ);

      const usersData = await Promise.all(
        usersSnapshot.docs.map(async (userDoc) => {
          const userData = userDoc.data();
          const uid = userDoc.id;
          const trainingsSnap = await getDocs(
            query(collection(db, 'users', uid, 'trainings'), orderBy('startedAt', 'desc'))
          );
          const trainings = trainingsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          const lastActivity = trainings.length > 0 ? toDate(trainings[0].startedAt) : null;
          return {
            uid,
            name: userData.profile?.name || '',
            email: userData.profile?.email || 'N/A',
            currentBelt: userData.profile?.currentBelt || '',
            schoolId: userData.profile?.schoolId || '',
            totalTrainings: trainings.length,
            lastActivity,
            trainings,
          };
        })
      );
      setUsers(usersData);

      if (schoolId) {
        const beltsSnap = await getDocs(collection(db, 'schools', schoolId, 'belts'));
        setSchoolBelts(
          beltsSnap.docs.map(d => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        );
      }

      if (role !== 'instructor') {
        const schoolsToLoad = schoolId
          ? [schoolId]
          : (await getDocs(collection(db, 'schools'))).docs.map(d => d.id);
        const allDrills = [];
        for (const sid of schoolsToLoad) {
          const snap = await getDocs(collection(db, 'schools', sid, 'drills'));
          snap.docs.forEach(d => allDrills.push({ id: d.id, schoolId: sid, ...d.data() }));
        }
        setDrills(allDrills);
      }
    } catch (err) {
      console.error('Error loading admin data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getBeltName = (beltId) => {
    if (!beltId) return null;
    const belt = schoolBelts.find(b => b.id === beltId);
    return belt ? (belt.nameEn || belt.nameHe || beltId) : beltId;
  };

  const handleUserClick = (uid, currentBelt) => {
    if (expandedUser === uid) {
      setExpandedUser(null);
      setPendingBelt('');
    } else {
      setExpandedUser(uid);
      setPendingBelt(currentBelt || '');
    }
  };

  const handleSaveBelt = async (uid) => {
    try {
      setSaving(true);
      await updateDoc(doc(db, 'users', uid), { 'profile.currentBelt': pendingBelt });
      setUsers(users.map(u => u.uid === uid ? { ...u, currentBelt: pendingBelt } : u));
      setExpandedUser(null);
      setPendingBelt('');
    } catch (err) {
      console.error('Error saving belt:', err);
      alert('Error saving: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSort = (col) => {
    setSortDir(sortCol === col && sortDir === 'asc' ? 'desc' : 'asc');
    setSortCol(col);
  };

  const getSortedUsers = () => {
    if (!sortCol) return users;
    return [...users].sort((a, b) => {
      let av, bv;
      if (sortCol === 'name')           { av = a.name.toLowerCase();  bv = b.name.toLowerCase(); }
      else if (sortCol === 'totalTrainings') { av = a.totalTrainings; bv = b.totalTrainings; }
      else if (sortCol === 'lastActivity') {
        if (!a.lastActivity && !b.lastActivity) return 0;
        if (!a.lastActivity) return 1;
        if (!b.lastActivity) return -1;
        av = a.lastActivity.getTime(); bv = b.lastActivity.getTime();
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleEditDrill = (drill) => {
    setEditingDrill(drill.id);
    setEditValues({
      nameEn: drill.nameEn || '',
      nameHe: drill.nameHe || '',
      descriptionEn: drill.descriptionEn || '',
      descriptionHe: drill.descriptionHe || '',
      category: drill.category || '',
    });
  };

  const handleSaveDrill = async (drill) => {
    try {
      await updateDoc(doc(db, 'schools', drill.schoolId, 'drills', drill.id), editValues);
      setDrills(drills.map(d => d.id === drill.id ? { ...d, ...editValues } : d));
      setEditingDrill(null);
      setEditValues({});
    } catch (err) {
      console.error('Error saving drill:', err);
      alert('Error saving drill: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const canEditDrills = userRole === 'super-admin' || userRole === 'school-admin';
  const hasBelts = schoolBelts.length > 0;
  const activeThisWeek = users.filter(
    u => u.lastActivity && (Date.now() - u.lastActivity) / 86400000 <= 7
  ).length;
  const totalTrainings = users.reduce((s, u) => s + u.totalTrainings, 0);
  const maxTrainings = Math.max(...users.map(u => u.totalTrainings), 1);

  const SortBtn = ({ col, label }) => (
    <button
      onClick={() => handleSort(col)}
      className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-400 hover:text-gray-600 transition-colors"
    >
      {label}
      <span className={`text-xs ${sortCol === col ? 'text-primary-600' : 'text-gray-300'}`}>
        {sortCol === col ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
      </span>
    </button>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-7 pb-16">

      {/* ── Header ───────────────────────────────────── */}
      <div className="flex items-start justify-between gap-3 mb-7 flex-wrap">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/home')}
            className="text-2xl hover:bg-gray-100 rounded-full p-2 transition-colors flex-shrink-0"
            title="Back"
          >
            ←
          </button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">Admin Panel</h1>
            <p className="text-sm text-gray-500 mt-1">Manage students, levels, and drills</p>
          </div>
        </div>
        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold bg-primary-100 text-primary-700 flex-shrink-0">
          <span className="w-2 h-2 rounded-full bg-primary-500" />
          {userRole}{currentSchoolId ? ` · ${currentSchoolId}` : ''}
        </span>
      </div>

      {/* ── Stat cards ───────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-7">
        <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm flex items-center gap-3 sm:gap-4">
          <div className="w-11 h-11 rounded-xl bg-primary-50 grid place-items-center text-xl flex-shrink-0">👥</div>
          <div>
            <div className="text-xs font-medium text-gray-500">Total students</div>
            <div className="text-2xl font-extrabold text-gray-900 tracking-tight">{users.length}</div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm flex items-center gap-3 sm:gap-4">
          <div className="w-11 h-11 rounded-xl bg-emerald-50 grid place-items-center text-xl flex-shrink-0">🟢</div>
          <div>
            <div className="text-xs font-medium text-gray-500">Active this week</div>
            <div className="text-2xl font-extrabold text-gray-900 tracking-tight">{activeThisWeek}</div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm flex items-center gap-3 sm:gap-4 col-span-2 sm:col-span-1">
          <div className="w-11 h-11 rounded-xl bg-orange-50 grid place-items-center text-xl flex-shrink-0">🥋</div>
          <div>
            <div className="text-xs font-medium text-gray-500">Total trainings</div>
            <div className="text-2xl font-extrabold text-gray-900 tracking-tight">{totalTrainings}</div>
          </div>
        </div>
      </div>

      {/* ── Students ─────────────────────────────────── */}
      <section className="mb-9">
        <div className="flex items-center justify-between mb-3">
          <span className="text-base font-bold text-gray-800">Students</span>
          <span className="text-xs font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
            {users.length} members
          </span>
        </div>

        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">

          {/* Column headers — desktop only */}
          <div
            className="hidden sm:grid items-center px-5 py-2.5 bg-gray-50 border-b border-gray-200 gap-2"
            style={{ gridTemplateColumns: GRID_COLS }}
          >
            <SortBtn col="name" label="Name" />
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">Level</span>
            <SortBtn col="totalTrainings" label="Sessions" />
            <SortBtn col="lastActivity" label="Last active" />
            <div />
          </div>

          {getSortedUsers().map((user) => {
            const isExpanded = expandedUser === user.uid;
            const status = actStatus(user.lastActivity);
            const beltName = getBeltName(user.currentBelt);
            const totalSecs = user.trainings.reduce((s, t) => s + (t.totalDurationSeconds || 0), 0);
            const totalHrs = (totalSecs / 3600).toFixed(1);

            return (
              <div key={user.uid} className={`border-b border-gray-100 last:border-b-0 ${isExpanded ? 'bg-primary-50/60' : ''}`}>

                {/* Desktop row */}
                <div
                  className="hidden sm:grid items-center px-5 py-3.5 cursor-pointer hover:bg-gray-50 transition-colors gap-2"
                  style={{ gridTemplateColumns: GRID_COLS }}
                  onClick={() => handleUserClick(user.uid, user.currentBelt)}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-9 h-9 rounded-full text-sm font-bold text-white grid place-items-center flex-shrink-0"
                      style={{ background: avatarGrad(user.uid) }}
                    >
                      {initials(user.name)}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-800 truncate">{user.name || '—'}</div>
                      <div className="text-xs text-gray-400 truncate">{user.email}</div>
                    </div>
                  </div>

                  <div>
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                      {beltName || <span className="text-gray-400 italic">— no level —</span>}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-gray-700">{user.totalTrainings}</span>
                    <div className="w-10 h-1.5 rounded-full bg-gray-100">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-primary-400 to-primary-600"
                        style={{ width: `${Math.min(100, (user.totalTrainings / maxTrainings) * 100)}%` }}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <StatusDot status={status} />
                    {relativeTime(user.lastActivity)}
                  </div>

                  <div className={`text-xs text-right transition-transform duration-200 ${isExpanded ? 'rotate-180 text-primary-500' : 'text-gray-400'}`}>
                    ▼
                  </div>
                </div>

                {/* Mobile row */}
                <div
                  className="flex sm:hidden items-center gap-3 px-4 py-3.5 cursor-pointer"
                  onClick={() => handleUserClick(user.uid, user.currentBelt)}
                >
                  <div
                    className="w-10 h-10 rounded-full text-sm font-bold text-white grid place-items-center flex-shrink-0"
                    style={{ background: avatarGrad(user.uid) }}
                  >
                    {initials(user.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-800">{user.name || '—'}</div>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 mt-0.5">
                      {beltName || '— no level —'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <StatusDot status={status} />
                    <span className="text-sm font-bold text-gray-700">{user.totalTrainings}</span>
                    <span className={`text-xs text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>▼</span>
                  </div>
                </div>

                {/* Expanded panel */}
                {isExpanded && (
                  <div className={`border-t border-primary-100 grid ${hasBelts ? 'sm:grid-cols-2' : 'grid-cols-1'}`}>

                    {/* Training history */}
                    <div className={`p-4 sm:p-5 ${hasBelts ? 'border-b sm:border-b-0 sm:border-r border-primary-100' : ''}`}>
                      <div className="text-xs font-bold uppercase tracking-widest text-primary-600 mb-3">
                        Training History
                      </div>
                      {user.trainings.length === 0 ? (
                        <p className="text-sm text-gray-400">No sessions yet.</p>
                      ) : (
                        <div className="relative pl-5">
                          <div className="absolute left-1.5 top-2 bottom-2 w-0.5 bg-primary-200" />
                          {user.trainings.slice(0, 8).map((t) => {
                            const d = toDate(t.startedAt);
                            const dateStr = d
                              ? d.toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                              : 'Unknown';
                            return (
                              <div key={t.id} className="flex items-center gap-3 py-1.5 relative">
                                <div className="absolute -left-5 w-3.5 h-3.5 rounded-full bg-white border-2 border-primary-400 z-10" />
                                <span className="text-xs font-medium text-gray-700 flex-1 min-w-0 truncate">{dateStr}</span>
                                <span className="text-xs text-gray-500 bg-white border border-gray-200 px-2 py-0.5 rounded-md flex-shrink-0">
                                  {fmtDuration(t.totalDurationSeconds)}
                                </span>
                              </div>
                            );
                          })}
                          {user.trainings.length > 8 && (
                            <p className="text-xs text-gray-400 mt-2">+ {user.trainings.length - 8} more sessions</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Edit form */}
                    {hasBelts && (
                      <div className="p-4 sm:p-5">
                        <div className="text-xs font-bold uppercase tracking-widest text-primary-600 mb-3">
                          Edit Student
                        </div>

                        <div className="flex gap-5 mb-4 pb-4 border-b border-primary-100">
                          <div>
                            <div className="text-lg font-extrabold text-gray-800 tracking-tight">{user.totalTrainings}</div>
                            <div className="text-xs text-gray-400 mt-0.5">Sessions</div>
                          </div>
                          <div>
                            <div className="text-lg font-extrabold text-gray-800 tracking-tight">{totalHrs}h</div>
                            <div className="text-xs text-gray-400 mt-0.5">Total time</div>
                          </div>
                          <div>
                            <div className="text-lg font-extrabold text-gray-800 tracking-tight">{relativeTime(user.lastActivity)}</div>
                            <div className="text-xs text-gray-400 mt-0.5">Last active</div>
                          </div>
                        </div>

                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                          Level / Belt
                        </label>
                        <select
                          value={pendingBelt}
                          onChange={e => setPendingBelt(e.target.value)}
                          className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm text-gray-700 bg-white outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                        >
                          <option value="">— no level —</option>
                          {schoolBelts.map(b => (
                            <option key={b.id} value={b.id}>{b.nameEn || b.nameHe || b.id}</option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-400 mt-1 mb-4">Levels are defined in school settings</p>

                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSaveBelt(user.uid)}
                            disabled={saving}
                            className="px-5 py-2 rounded-xl text-sm font-semibold bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 transition-colors"
                          >
                            {saving ? 'Saving…' : 'Save changes'}
                          </button>
                          <button
                            onClick={() => { setExpandedUser(null); setPendingBelt(''); }}
                            className="px-4 py-2 rounded-xl text-sm font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Drills ───────────────────────────────────── */}
      {canEditDrills && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <span className="text-base font-bold text-gray-800">Drills</span>
            <span className="text-xs font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
              {drills.length} drills
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {drills.map((drill) => (
              <div
                key={`${drill.schoolId}-${drill.id}`}
                className={`bg-white rounded-2xl p-4 sm:p-5 shadow-sm flex flex-col gap-3 transition-all ${
                  editingDrill === drill.id
                    ? 'ring-2 ring-primary-400'
                    : 'hover:shadow-md hover:-translate-y-0.5'
                }`}
              >
                {editingDrill === drill.id ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-semibold text-gray-500 mb-1 block">Name (EN)</label>
                        <input
                          className="w-full px-3 py-1.5 border-2 border-gray-200 rounded-lg text-sm focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none transition-all"
                          value={editValues.nameEn}
                          onChange={e => setEditValues({ ...editValues, nameEn: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-500 mb-1 block">Name (HE)</label>
                        <input
                          dir="rtl"
                          className="w-full px-3 py-1.5 border-2 border-gray-200 rounded-lg text-sm focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none transition-all"
                          value={editValues.nameHe}
                          onChange={e => setEditValues({ ...editValues, nameHe: e.target.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 mb-1 block">Description (EN)</label>
                      <textarea
                        className="w-full px-3 py-1.5 border-2 border-gray-200 rounded-lg text-sm min-h-[56px] resize-y focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none transition-all"
                        value={editValues.descriptionEn}
                        onChange={e => setEditValues({ ...editValues, descriptionEn: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 mb-1 block">Description (HE)</label>
                      <textarea
                        dir="rtl"
                        className="w-full px-3 py-1.5 border-2 border-gray-200 rounded-lg text-sm min-h-[56px] resize-y focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none transition-all"
                        value={editValues.descriptionHe}
                        onChange={e => setEditValues({ ...editValues, descriptionHe: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 mb-1 block">Category</label>
                      <input
                        className="w-full px-3 py-1.5 border-2 border-gray-200 rounded-lg text-sm focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none transition-all"
                        value={editValues.category}
                        onChange={e => setEditValues({ ...editValues, category: e.target.value })}
                      />
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={() => handleSaveDrill(drill)}
                        className="px-4 py-1.5 rounded-lg text-sm font-semibold bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => { setEditingDrill(null); setEditValues({}); }}
                        className="px-4 py-1.5 rounded-lg text-sm font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-gray-800">{drill.nameEn || 'N/A'}</div>
                        {drill.nameHe && (
                          <div className="text-xs text-gray-500 mt-0.5" dir="rtl">{drill.nameHe}</div>
                        )}
                      </div>
                      {drill.category && (
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-accent-500 text-white flex-shrink-0">
                          {drill.category}
                        </span>
                      )}
                    </div>
                    {drill.descriptionEn && (
                      <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">{drill.descriptionEn}</p>
                    )}
                    <div className="flex items-center justify-between mt-auto pt-2 border-t border-gray-100">
                      <span className="text-xs text-gray-400">🏫 {drill.schoolId}</span>
                      <button
                        onClick={() => handleEditDrill(drill)}
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-primary-200 text-primary-600 hover:bg-primary-50 hover:border-primary-400 transition-colors"
                      >
                        Edit
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
